import { getAuthCapabilities, type AuthCapabilities } from '@/api/auth';
import { useSyncExternalStore } from 'react';

/**
 * Defaults describe an LDAP deployment, i.e. every self-service flow available.
 *
 * Failing open is deliberate. The backend still refuses each flow in read-only
 * mode and says why, so a transient failure to load capabilities costs one
 * confusing click; failing closed would silently hide registration from a
 * working deployment.
 */
export const DEFAULT_AUTH_CAPABILITIES: AuthCapabilities = {
  self_service: {
    registration: true,
    password_reset: true,
    account_deletion: true,
    access_verification: true,
  },
  login_accepts_username: true,
};

let current: AuthCapabilities = DEFAULT_AUTH_CAPABILITIES;
let loaded = false;
const listeners = new Set<() => void>();

const emit = () => {
  listeners.forEach((listener) => listener());
};

/**
 * Fetches the deployment's self-service capabilities once. Safe to call from
 * module scope; resolution notifies every subscriber.
 */
export const loadAuthCapabilities = async (): Promise<AuthCapabilities> => {
  try {
    const res = await getAuthCapabilities();
    if (res?.ok && res.data) {
      current = {
        ...DEFAULT_AUTH_CAPABILITIES,
        ...res.data,
        // Merge field by field so a backend that omits a flag cannot switch a
        // flow off by accident.
        self_service: { ...DEFAULT_AUTH_CAPABILITIES.self_service, ...res.data.self_service },
      };
      loaded = true;
    }
  } catch (error) {
    // Never rethrow. This is invoked fire-and-forget at module scope, so an
    // escaping rejection would surface as an unhandled promise rejection
    // instead of degrading to the permissive defaults. The API layer catches
    // its own errors, but an interceptor or a future refactor could bypass
    // that, and a missing sign-up link is a better failure than a crashed boot.
    console.error('Failed to load auth capabilities, keeping defaults:', error);
  }
  emit();
  return current;
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Reactive read. `current` is replaced wholesale and never mutated, so the
 * snapshot identity is stable between loads.
 */
export const useAuthCapabilities = (): AuthCapabilities =>
  useSyncExternalStore(subscribe, () => current, () => DEFAULT_AUTH_CAPABILITIES);

/**
 * Non-reactive read, for code paths that render long after startup -- by which
 * point the single fetch has settled -- and where adding a hook would mean
 * reordering an existing component's hook list.
 */
export const getAuthCapabilitiesSnapshot = (): AuthCapabilities => current;

export const areAuthCapabilitiesLoaded = (): boolean => loaded;
